/*
 * A casca do tema: cabeçalho de largura total com o hambúrguer, o logo e a
 * busca centralizada; barra lateral global à esquerda; e o conteúdo num cartão
 * sobre o fundo tingido.
 *
 * O layout do core (forum.blade.php) não tem onde pôr uma barra lateral global,
 * então ela ganha um <aside> próprio dentro do #app, montado logo depois que o
 * core monta o resto. No celular o mesmo <aside> vira o painel do hambúrguer
 * (o .menu-panel), por cima de tudo.
 */
import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import PageStructure from 'flarum/forum/components/PageStructure';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import Icon from 'flarum/common/components/Icon';
import classList from 'flarum/common/utils/classList';
import CanelaSidebar from '../components/CanelaSidebar';
import HeaderToggle from '../components/HeaderToggle';
import HeaderCenter, { watchHeaderTopic } from './HeaderCenter';
import { watchColoredAccents } from './coloredAccents';
import installShortcuts from './shortcuts';
import extendHeader from './extendHeader';
import { applyBackground, forumBackground } from './backgrounds';
import { readPreview } from './preview';
import installThemePreview from './themePreview';
import installModalLayout from './modalLayout';
import { forumBool, t } from '../utils/settings';
import { SHELL_CLASS, applySidebarState, closeOverlaySidebar, watchViewport } from '../utils/sidebarState';
import { installColorMode } from './colorMode';
import installDropdownArrow from './dropdownArrow';
import installAssetsAlert from './assetsAlert';
import { relocateRoutes, yieldConflictingRoutes } from '../utils/routeConflicts';

/** Páginas em que o hero vai para dentro do cartão. */
const INLINE_HERO_PAGES = /\b(DiscussionPage|UserPage)\b/;
export default function installShell() {
  installDropdownArrow();
  installAssetsAlert();

  // Antes de o app montar o roteador (todas as extensões já registraram as
  // suas páginas): os caminhos que outra extensão também usa ficam com ela.
  override(app, 'mount', function (original, ...args) {
    relocateRoutes();
    yieldConflictingRoutes();
    return original(...args);
  });

  // Estende a instância, e não o protótipo: o core 2.0 não exporta a classe
  // ForumApplication no registro de módulos. O initializer roda antes do mount.
  extend(app, 'mount', function () {
    const appElement = document.getElementById('app');
    if (!appElement) return;

    // O rascunho do admin (links "Pré-visualizar"), antes das classes e do fundo.
    readPreview();
    const sidebarEnabled = forumBool('canelaSidebarEnabled', true);
    appElement.classList.add(...classList(SHELL_CLASS, {
      'Canela--pillButtons': forumBool('canelaPillButtons', true),
      // Fundo com tinta (tudo menos o liso): sem ela, o cartão ganha borda.
      'Canela--tinted': forumBackground().style !== 'plain',
      'Canela--card': forumBool('canelaContentCard', true),
      'Canela--noSidebar': !sidebarEnabled,
      'Canela--headerSearch': forumBool('canelaHeaderSearch', true),
      'Canela--noEventPosts': !forumBool('canelaTopicEventPosts', true),
      'Canela--stickyAvatar': forumBool('canelaTopicStickyAvatar', false),
      // A linha do tempo ligando os avatares (less/forum/PostDirect.less).
      'Canela--postDirect': app.forum.attribute('canelaPostStyle') === 'direct',
      // Ícones antes do título (lista e discussão) cada um na sua cor.
      'Canela--statusColored': app.forum.attribute('canelaListStatusIcons') === 'colored'
    }).split(' '));

    // O fundo e o raio dos botões no <html> (o PreBootShell já os pôs; aqui
    // para o caso de a página vir sem ele). Em /login e /signup vale o fundo
    // dessas páginas (features/auth troca ao entrar e sair delas).
    applyBackground(document.documentElement.classList.contains('CanelaAuthRoute') ? 'auth' : 'forum');
    document.documentElement.classList.toggle('CanelaRoot--pill', forumBool('canelaPillButtons', true));
    mountHeader(sidebarEnabled);
    watchColoredAccents();
    installColorMode();
    if (sidebarEnabled) {
      mountSidebar(appElement);
      applySidebarState();
      watchViewport();
    }
    mountBackdrop(appElement);
    if (forumBool('canelaKeyboardShortcuts', true)) installShortcuts();
  });
  extendHeader();
  installThemePreview();
  installModalLayout();
  installInlineHero();
  removeDiscussionPane();
}

/**
 * No celular os menus do painel (Mais, paleta, modo de cor) são a folha que
 * sobe do pé da tela, com `position: fixed`. O Safari do iOS recorta um
 * elemento fixo pelos limites de quem rola por cima dele: com o painel como
 * área de rolagem (`overflow-y: auto`), a folha saía do tamanho do painel, e
 * não da tela. Enquanto um menu está aberto o painel deixa de rolar (a classe
 * tira o overflow) e o conteúdo fica parado onde estava; ao fechar, a rolagem
 * volta para o mesmo ponto.
 */
function keepPanelMenusOnScreen(aside) {
  const OPEN = 'CanelaSidebar--menuOpen';
  let scrollTop = 0;
  $(aside).on('shown.bs.dropdown', '.Dropdown', () => {
    if (!document.getElementById('app')?.classList.contains('Canela--sidebarOverlay')) return;
    scrollTop = aside.scrollTop;
    aside.style.setProperty('--canela-panel-scroll', `${scrollTop}px`);
    aside.classList.add(OPEN);
  });
  $(aside).on('hidden.bs.dropdown', '.Dropdown', () => {
    if (!aside.classList.contains(OPEN) || aside.querySelector('.Dropdown.open')) return;
    aside.classList.remove(OPEN);
    aside.style.removeProperty('--canela-panel-scroll');
    aside.scrollTop = scrollTop;
  });
}
function mountSidebar(appElement) {
  const aside = document.createElement('aside');
  aside.id = 'canela-sidebar';
  aside.className = 'CanelaSidebar';
  aside.setAttribute('aria-label', t('sidebar.label'));

  // Depois do link "pular para o conteúdo", que precisa ser o primeiro foco.
  appElement.insertBefore(aside, document.getElementById('app-navigation'));
  m.mount(aside, {
    view: () => m(CanelaSidebar, null)
  });
  keepPanelMenusOnScreen(aside);

  // No painel sobreposto (tablet), escolher um destino fecha o painel.
  aside.addEventListener('click', event => {
    if (event.target.closest('a')) closeOverlaySidebar();
  });

  // Esc fecha o painel do celular.
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') closeOverlaySidebar();
  });
  const backdrop = document.createElement('div');
  backdrop.className = 'CanelaSidebar-backdrop';
  backdrop.addEventListener('click', () => closeOverlaySidebar());
  appElement.insertBefore(backdrop, aside.nextSibling);
}

/** As peças coloridas e desfocadas do fundo. */
const BACKDROP_TILES = [['plane', 'fas fa-paper-plane'], ['chat', 'fas fa-comment-dots'], ['quote', 'fas fa-quote-left'], ['bell', 'fas fa-bell'], ['sparkles', 'fas fa-wand-magic-sparkles'], ['reactions', 'fas fa-face-smile']];
function mountBackdrop(appElement) {
  const backdrop = document.createElement('div');
  backdrop.className = 'CanelaBackdrop';
  backdrop.setAttribute('aria-hidden', 'true');
  appElement.insertAdjacentElement('afterend', backdrop);
  m.render(backdrop, BACKDROP_TILES.map(([name, icon]) => m("div", {
    className: `CanelaBackdrop-tile CanelaBackdrop-tile--${name}`
  }, m(Icon, {
    name: icon
  }))));
}
function mountHeader(sidebarEnabled) {
  const container = document.querySelector('#header > .container');
  const title = container?.querySelector('.Header-title');
  if (!container || !title) return;

  // Os encaixes podem já existir: o script do <head> (recurso `preload`) os cria
  // antes da primeira pintura, para o logo não pular de lugar no boot.
  if (sidebarEnabled) {
    let toggleSlot = container.querySelector(':scope > .CanelaHeader-toggleSlot');
    if (!toggleSlot) {
      toggleSlot = document.createElement('div');
      toggleSlot.className = 'CanelaHeader-toggleSlot';
      container.insertBefore(toggleSlot, title);
    }
    m.mount(toggleSlot, HeaderToggle);
  }

  // O meio do cabeçalho existe mesmo sem a busca: é onde o título do tópico
  // aparece ao rolar.
  let centerSlot = container.querySelector(':scope > .CanelaHeader-center');
  if (!centerSlot) {
    centerSlot = document.createElement('div');
    centerSlot.className = 'CanelaHeader-search CanelaHeader-center';
    container.insertBefore(centerSlot, document.getElementById('header-secondary'));
  }
  m.mount(centerSlot, HeaderCenter);
  watchHeaderTopic();
  if (!forumBool('canelaShowTitleWithLogo', false)) {
    container.classList.add('CanelaHeader--logoOnly');
  }
}

/**
 * O título do tópico e a capa do perfil ficam dentro do cartão,
 * e não sobre o fundo como o hero do Flarum. Em vez de reescrever as páginas,
 * o hero muda de lugar no PageStructure, que todas elas usam.
 */
/**
 * O painel lateral com a lista de discussões que o Flarum abre na página do
 * tópico (o "pane") sai do PageStructure e fica
 * desligado, para não aparecer nem ao passar o mouse na borda.
 */
function removeDiscussionPane() {
  override(PageStructure.prototype, 'rootItems', function (original) {
    const items = original();
    items.remove('pane');
    return items;
  });
  extend(DiscussionPage.prototype, 'oninit', function () {
    app.pane?.disable();
  });
}
function installInlineHero() {
  const isInline = structure => INLINE_HERO_PAGES.test(structure.attrs.className || '');
  override(PageStructure.prototype, 'mainItems', function (original) {
    const items = original();
    if (isInline(this)) items.remove('hero');
    return items;
  });
  override(PageStructure.prototype, 'containerItems', function (original) {
    const items = original();
    if (isInline(this)) {
      items.add('hero', m("div", {
        className: "Page-hero Page-hero--inline"
      }, this.attrs.hero && this.attrs.hero() || null), 200);
    }
    return items;
  });

  // O cartão com o hero dentro ganha uma classe (Shell.less), no lugar de
  // `.Page-container:has(> .Page-hero--inline)`: o `:has()` no ancestral do
  // tópico inteiro fazia o navegador recalcular o estilo a cada post
  // carregado na rolagem (~150 ms por gesto, medido no /d/1).
  extend(PageStructure.prototype, 'container', function (vnode) {
    if (isInline(this) && vnode?.attrs) vnode.attrs.className = `${vnode.attrs.className || ''} Page-container--inlineHero`;
  });
}
flarum.reg.add('ramon-canela', 'forum/shell/installShell', installShell);